import { BuildClass, Do, fsmoData } from '../../../../universal';
import { _, React } from '../../../lib';
import { FormButtonSet } from '../buttons';
import { CJSX, useMemoCompare } from '../meta-types';
import { FormFillButton } from './llm-fill';
import { FormManager, FormSaveOutcome } from './manager';
const FormComponentInner = (props, ref) => {
    const isMultiple = 'multiple' in props && props.multiple === true;
    const getDefaultValueFn = isMultiple
        ? () => {
            const defaults = props.defaultValues?.();
            return defaults?.[0] ?? props.defaultValue?.() ?? {};
        }
        : props.defaultValue;
    const manager = new FormManager({
        fields: props.fields,
        formFixer: props.formFixer,
        disableFormFill: props.disableFormFill,
        getDefaultValues: getDefaultValueFn,
    });
    const input = isMultiple ? props.values : props.value;
    const defaultState = useMemoCompare(() => manager.defaultState, manager.defaultState);
    const collapseDefaultValues = React.useCallback((defaults) => {
        if (defaults.length <= 1) {
            return { template: defaults[0] ?? defaultState, clashes: {} };
        }
        const template = defaults[0] ?? defaultState;
        const clashes = {};
        _.forEach(props.fields, (_field, key) => {
            const values = defaults.map(d => d[key]);
            const allEqual = values.every(v => _.isEqual(v, values[0]));
            if (!allEqual) {
                clashes[key] = true;
            }
        });
        return { template, clashes };
    }, [defaultState, props.fields]);
    const hasExplicitBaseline = isMultiple
        ? Boolean(props.defaultValues)
        : Boolean(props.defaultValue);
    const nextDefaultValuesList = isMultiple
        ? (props.defaultValues?.() ?? [input])
        : [hasExplicitBaseline ? defaultState : (input ?? defaultState)];
    const defaultValuesList = useMemoCompare(() => nextDefaultValuesList, nextDefaultValuesList);
    const defaultCollapsed = React.useMemo(() => collapseDefaultValues(defaultValuesList), [collapseDefaultValues, defaultValuesList]);
    const baselineRecords = React.useMemo(() => defaultValuesList.length ? defaultValuesList : [defaultCollapsed.template], [defaultCollapsed.template, defaultValuesList]);
    const normaliseRecords = React.useCallback((val) => {
        if (isMultiple) {
            const arr = val ?? [];
            return arr.length ? arr : [defaultCollapsed.template];
        }
        const single = val ?? defaultCollapsed.template;
        return [single];
    }, [defaultCollapsed.template, isMultiple]);
    const [records, setRecords] = React.useState(() => normaliseRecords(input));
    const originalRecordsRef = React.useRef(_.cloneDeep(baselineRecords));
    const pendingInternalRecordsRef = React.useRef(null);
    const lastPropBaselineRecordsRef = React.useRef(_.cloneDeep(baselineRecords));
    const [touchedKeys, setTouchedKeys] = React.useState(new Set());
    const firstRecord = records[0];
    if (!isMultiple && !firstRecord) {
        throw new Error('No record exists for non-multi mode');
    }
    const nextPropRecords = normaliseRecords(input);
    const propRecords = useMemoCompare(() => nextPropRecords, nextPropRecords);
    React.useEffect(() => {
        const isMirroredInternalUpdate = pendingInternalRecordsRef.current != null &&
            _.isEqual(propRecords, pendingInternalRecordsRef.current);
        const didPropBaselineChange = hasExplicitBaseline &&
            !_.isEqual(baselineRecords, lastPropBaselineRecordsRef.current);
        setRecords(_.cloneDeep(propRecords));
        lastPropBaselineRecordsRef.current = _.cloneDeep(baselineRecords);
        pendingInternalRecordsRef.current = null;
        if (isMirroredInternalUpdate && !didPropBaselineChange) {
            return;
        }
        originalRecordsRef.current = _.cloneDeep(baselineRecords);
        setTouchedKeys(new Set());
    }, [baselineRecords, hasExplicitBaseline, propRecords]);
    const pushRecords = React.useCallback((next, touched) => {
        pendingInternalRecordsRef.current = _.cloneDeep(next);
        setRecords(next);
        if (isMultiple) {
            props.onUpdate(next);
            if (touched?.length) {
                setTouchedKeys(prev => new Set([...prev, ...touched]));
            }
        }
        else {
            const record = next[0];
            if (record) {
                props.onUpdate(record);
            }
        }
    }, [isMultiple, props.onUpdate]);
    const overlay = React.useMemo(() => {
        if (!isMultiple) {
            return {
                state: firstRecord,
                clash: {},
            };
        }
        const template = defaultCollapsed.template;
        const map = {};
        const merged = {};
        _.forEach(props.fields, (_field, key) => {
            const values = records.map(rec => rec[key]);
            const allEqual = values.every(v => _.isEqual(v, values[0]));
            merged[key] = allEqual ? values[0] : template[key];
            map[key] = Boolean((!allEqual || defaultCollapsed.clashes[key]) && !touchedKeys.has(key));
        });
        return {
            state: merged,
            clash: map,
        };
    }, [defaultCollapsed, isMultiple, props.fields, records, touchedKeys]);
    const state = isMultiple ? overlay.state : firstRecord;
    const clashMask = isMultiple ? overlay.clash : {};
    const hasChanged = isMultiple
        ? touchedKeys.size > 0
        : !_.isEqual(firstRecord, originalRecordsRef.current[0] ?? defaultCollapsed.template);
    const refFormElement = React.useRef(null);
    const [, setFocused] = React.useState(null);
    const [isLoading, setIsLoading] = React.useState(false);
    const [message, setMessageRaw] = React.useState({
        msg: '',
        type: 'bad',
    });
    const timerRef = React.useRef(null);
    const setMessage = (msg, type) => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        setMessageRaw({
            msg: msg,
            type: type ?? 'bad',
        });
    };
    const applyOverlayChanges = React.useCallback((newOverlay) => {
        const changed = _.pickBy(newOverlay, (v, k) => !_.isEqual(v, state?.[k]));
        if (_.isEmpty(changed)) {
            return;
        }
        if (isMultiple) {
            const next = records.map(rec => ({ ...rec, ...changed }));
            pushRecords(next, _.keys(changed));
        }
        else {
            const currentRecord = records[0] ?? defaultCollapsed.template;
            pushRecords([{ ...currentRecord, ...changed }]);
        }
    }, [isMultiple, pushRecords, records, state, defaultCollapsed.template]);
    const onChangeState = props.onChangeState;
    React.useEffect(() => {
        onChangeState?.({ hasChanges: hasChanged, isLoading });
    }, [hasChanged, isLoading, onChangeState]);
    React.useEffect(() => {
        props.onMount?.();
    }, [props.onMount]);
    const components = fsmoData(props.fields, {
        map: (_field, key) => [
            key,
            manager.renderComponentFormRow({
                currentState: state ?? defaultCollapsed.template,
                key: key,
                lblWidth: props.lblWidth,
                highlightChanges: !props.disableChangeHighlights,
                disabled: props.disabled ?? false,
                clashing: clashMask[key] ?? false,
                onUpdate: updater => {
                    const next = records.map(rec => updater(rec));
                    pushRecords(next, isMultiple ? [key] : undefined);
                    setMessage('');
                },
                onFocus: () => {
                    setFocused(key);
                },
                onBlur: () => {
                    setFocused(null);
                },
            }),
        ],
        valuesOnly: true,
    });
    React.useImperativeHandle(ref, () => ({
        manager,
        currentState: records,
        setCurrentState: (newState) => {
            pushRecords(newState);
        },
        setFocused,
        hasUnsavedChanges: hasChanged,
    }));
    const buttons = !props.excludeButtons && (React.createElement(FormButtonSet, { loading: isLoading, msg: message.msg, tick: message.type == 'good', neutral: message.type == 'neutral', widths: props.buttonWidth, buttons: [
            {
                lbl: props.lblSave ?? 'Save',
                type: 'submit',
                title: hasChanged
                    ? (props.lblSaveTooltip ?? 'Save changes')
                    : 'Nothing to save',
                onClick: () => {
                },
            },
            {
                lbl: 'Reset',
                hidden: props.excludeReset ?? false,
                title: hasChanged ? 'Resets the form' : 'Nothing to reset',
                disabled: !hasChanged,
                onClick: () => {
                    const resetValues = _.cloneDeep(originalRecordsRef.current);
                    pushRecords(resetValues);
                    setTouchedKeys(new Set());
                    setMessage('');
                },
            },
            ...(props.extraActions ?? []).map(x => ({
                lbl: x.lbl,
                type: x.type,
                onClick: () => {
                    x.onClick({
                        state: manager.getPublicState(state ?? defaultCollapsed.template),
                        setLoading: setIsLoading,
                        setMessage,
                        markAsSuccessful: () => {
                            setMessage('', 'good');
                            if (timerRef.current) {
                                clearTimeout(timerRef.current);
                            }
                            timerRef.current = Number(setTimeout(() => {
                                setMessage('');
                            }, 2000));
                            props.onSuccessfulSave?.();
                        },
                    });
                },
                disabled: Do(() => {
                    if (x.requiresChanges && !hasChanged) {
                        return true;
                    }
                    if (x.requiresNoChanges && hasChanged) {
                        return true;
                    }
                    return (x.checkIfUsable?.(manager.getPublicState(state ?? defaultCollapsed.template)) ?? false);
                }),
            })),
        ] }));
    const submitEvent = async () => {
        setIsLoading(true);
        setMessage('');
        const fixed = records.map(record => {
            let next = { ...record };
            _.forEach(props.fields, (_field, key) => {
                next = manager.updateValue({
                    currentState: next,
                    key,
                    value: next[key],
                    fullFieldFix: true,
                    depth: 0,
                });
            });
            return next;
        });
        const publicCurrent = fixed.map((item) => manager.getPublicState(item));
        const publicOriginal = originalRecordsRef.current.map((item) => manager.getPublicState(item));
        if (!props.allowSavingWithoutChanges &&
            _.isEqual(publicCurrent, publicOriginal)) {
            setIsLoading(false);
            setMessage('Nothing has changed', 'neutral');
            return;
        }
        const errors = _.flatMap(fixed, item => manager.getValidationErrors(item));
        if (errors.length) {
            setIsLoading(false);
            setMessage(errors.join('\n'));
            return;
        }
        if (isMultiple) {
            pushRecords(fixed);
        }
        else {
            const fixedRecord = fixed[0] ?? defaultCollapsed.template;
            pushRecords([fixedRecord]);
        }
        const delta = (isMultiple
            ? _.pick(manager.getPublicState(state ?? defaultCollapsed.template), Array.from(touchedKeys))
            : _.pickBy(publicCurrent[0], (value, key) => !_.isEqual(value, publicOriginal[0]?.[key])));
        const result = await new Promise(resolve => {
            const callback = (res) => {
                resolve(res === true
                    ? { status: FormSaveOutcome.Good, message: '' }
                    : { status: FormSaveOutcome.Error, message: res });
            };
            if (isMultiple) {
                props.onSave({ current: publicCurrent, original: publicOriginal, delta }, callback);
            }
            else {
                const currentValue = publicCurrent[0] ??
                    manager.getPublicState(defaultCollapsed.template);
                const originalValue = publicOriginal[0] ??
                    manager.getPublicState(defaultCollapsed.template);
                props.onSave({
                    current: currentValue,
                    original: originalValue,
                    delta,
                }, callback);
            }
        });
        setIsLoading(false);
        if (result.status == FormSaveOutcome.Error) {
            setMessage(result.message);
            return;
        }
        const timeout = result.status == FormSaveOutcome.Neutral ? 1000 : 4000;
        const msgType = result.status == FormSaveOutcome.Neutral ? 'neutral' : 'good';
        setMessage(result.message, msgType);
        timerRef.current = Number(setTimeout(() => {
            setMessage('');
        }, timeout));
        if (result.status == FormSaveOutcome.Good) {
            originalRecordsRef.current = _.cloneDeep(fixed);
            setTouchedKeys(new Set());
            props.onSuccessfulSave?.();
        }
    };
    return (React.createElement("form", { tabIndex: -1, ref: refFormElement, className: BuildClass([props.className, 'font-condensed relative']), action: undefined, onSubmit: e => {
            e.preventDefault();
            submitEvent().catch(console.error);
        }, onKeyDown: e => {
            if (e.ctrlKey && e.code === 'KeyS') {
                e.stopPropagation();
                e.preventDefault();
                submitEvent().catch(console.error);
                return;
            }
            manager.hotkeyPress(e, {
                currentState: state ?? defaultCollapsed.template,
                setState: applyOverlayChanges,
                formPrompt: props.formPrompt,
                voicePrompt: props.voicePrompt,
                containingElement: refFormElement.current ?? document.body,
            });
        }, onKeyUp: e => {
            manager.hotkeyRelease(e);
        } },
        props.contentClassName ? (React.createElement("div", { className: BuildClass(props.contentClassName) }, components)) : (components),
        buttons,
        React.createElement(CJSX, { cond: !props.disableFormFill },
            React.createElement("div", { className: BuildClass({
                    'absolute right-0': true,
                    'bottom-8': !(props.excludeButtons ?? false),
                    '-bottom-8': props.excludeButtons ?? false,
                }) },
                React.createElement(FormFillButton, { manager: manager, currentState: state ?? defaultCollapsed.template, setState: applyOverlayChanges, formPrompt: props.formPrompt, voicePrompt: props.voicePrompt, containingElement: refFormElement.current ?? document.body })))));
};
export const FormComponent = React.forwardRef(FormComponentInner);
//# sourceMappingURL=render.js.map